import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const CustomerContext = createContext(null);

// Separate axios instance: customer Bearer token (never the admin one)
export const customerApi = axios.create({ baseURL: API });
customerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("cfm_customer_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiError(detail, fallback = "Κάτι πήγε στραβά. Δοκίμασε ξανά.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null); // null = checking, false = guest, object = logged in
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("cfm_customer_token");
    if (!token) {
      setCustomer(false);
      setReady(true);
      return;
    }
    customerApi
      .get("/account/me")
      .then((res) => setCustomer(res.data))
      .catch(() => {
        localStorage.removeItem("cfm_customer_token");
        setCustomer(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await customerApi.post("/account/login", { email, password });
    localStorage.setItem("cfm_customer_token", res.data.access_token);
    setCustomer(res.data.customer);
    return res.data.customer;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await customerApi.post("/account/register", payload);
    localStorage.setItem("cfm_customer_token", res.data.access_token);
    setCustomer(res.data.customer);
    return res.data.customer;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("cfm_customer_token");
    setCustomer(false);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await customerApi.get("/account/me");
      setCustomer(res.data);
    } catch {
      logout();
    }
  }, [logout]);

  return (
    <CustomerContext.Provider value={{ customer, ready, login, register, logout, refresh, setCustomer }}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx;
};
